import React, { useState, useEffect } from "react";
import { RequireStaff } from "@/components/RequireStaff";
import { useLanguage } from "@/contexts/LanguageContext";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Search, Loader2, Handshake, Mail, Phone, CheckCircle2, Ban, ShieldCheck, ShieldAlert } from "lucide-react";
import { collection, getDocs, query, where, doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { toast } from "@/hooks/use-toast";

const partnerT = {
  en: {
    title: "AcquizGroup Partners",
    subtitle: "Direct partners of AcquizGroup — lawyers, notaries, CPAs, lenders, and consultants.",
    search: "Search partners...",
    noPartners: "No partners found",
    verify: "Verify",
    suspend: "Suspend",
    verified: "Verified",
    suspended: "Suspended",
    pending: "Pending",
    tier: "Tier",
    bgCheck: "Background",
    didit: "Didit",
    specializations: "Specializations",
    licenses: "Licenses",
  },
  fr: {
    title: "Partenaires AcquizGroup",
    subtitle: "Partenaires directs d'AcquizGroup — avocats, notaires, CPA, prêteurs et consultants.",
    search: "Rechercher un partenaire...",
    noPartners: "Aucun partenaire trouvé",
    verify: "Vérifier",
    suspend: "Suspendre",
    verified: "Vérifié",
    suspended: "Suspendu",
    pending: "En attente",
    tier: "Tier",
    bgCheck: "Antécédents",
    didit: "Didit",
    specializations: "Spécialisations",
    licenses: "Licences",
  },
};

const statusColor = (s?: string) => {
  const v = (s || "").toLowerCase();
  if (v === "verified" || v === "passed" || v === "approved") return "bg-emerald-500/20 text-emerald-300 border-emerald-500/40";
  if (v === "suspended" || v === "failed" || v === "declined") return "bg-red-500/20 text-red-300 border-red-500/40";
  if (v === "in review" || v === "pending" || v === "review") return "bg-amber-500/20 text-amber-300 border-amber-500/40";
  return "bg-white/10 text-gray-300 border-white/20";
};

export default function StaffPartners() {
  const { language } = useLanguage();
  const labels = partnerT[language] || partnerT.en;
  const [partners, setPartners] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchPartners = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(query(collection(db, "users"), where("roles", "array-contains", "partner")));
      setPartners(snap.docs.map((d) => ({ id: d.id, uid: d.id, ...d.data() })));
    } catch (err) {
      console.error("Error loading partners:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartners();
  }, []);

  // CORRECTIF — écrit maintenant aussi `status` (active/suspended), pas seulement
  // manualVerificationStatus. RequireAccountActive dépend de `status` pour
  // débloquer l'accès au dashboard partner ; sans ça, "Vérifier" ne changeait
  // rien côté accès réel.
  const setStatus = async (uid: string, status: "verified" | "suspended") => {
    setBusyId(uid);
    try {
      await updateDoc(doc(db, "users", uid), {
        manualVerificationStatus: status,
        manualVerificationAt: serverTimestamp(),
        status: status === "verified" ? "active" : "suspended",
      });
      setPartners((prev) =>
        prev.map((p) => (p.uid === uid ? { ...p, manualVerificationStatus: status } : p))
      );
      toast({ title: status === "verified" ? labels.verified : labels.suspended });
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  const filtered = partners.filter((p) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (p.displayName || p.name || "").toLowerCase().includes(q) ||
      (p.email || "").toLowerCase().includes(q)
    );
  });

  return (
    <RequireStaff>
      <div className="space-y-4 font-['Geist',sans-serif]">
        <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-white flex items-center gap-2">
              <Handshake className="w-5 h-5 text-amber-400" /> {labels.title}
              {!loading && <span className="text-gray-400 font-normal text-sm">({filtered.length})</span>}
            </h1>
            <p className="text-sm text-gray-400 mt-1">{labels.subtitle}</p>
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
            <Input
              placeholder={labels.search}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500"
            />
          </div>
        </header>

        <div className="rounded-xl overflow-hidden border border-white/10 bg-white/[0.03]">
          {loading ? (
            <div className="flex justify-center p-12">
              <Loader2 className="w-5 h-5 animate-spin text-white/30" />
            </div>
          ) : (
            <ScrollArea className="max-h-[calc(100vh-260px)]">
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
                {filtered.map((p) => {
                  const reg = p.registrationData || {};
                  // CORRECTIF — licenses et specializations sont des tableaux
                  // d'OBJETS dans Firestore, pas des strings. Voir structure
                  // réelle : licenses[].{number, details, jurisdictions[]},
                  // specializations[].{expertises[], industry, experienceYears,
                  // spokenLanguages[]}.
                  const specs: any[] = reg.specializations || [];
                  const licenses: any[] = reg.licenses || [];
                  const tier = p.partnerSubscription?.baseTier;
                  const bg = p.backgroundCheckStatus;
                  const didit = p.diditVerification?.status;
                  const mvStatus = p.manualVerificationStatus;

                  return (
                    <div
                      key={p.uid}
                      className="bg-white/5 rounded-xl p-4 border border-white/10 hover:border-amber-500/30 transition-all flex flex-col gap-2"
                    >
                      <div className="flex justify-between items-start">
                        <h3 className="font-semibold text-white text-sm">
                          {p.displayName || p.name || p.email || "—"}
                        </h3>
                        {mvStatus && (
                          <Badge variant="outline" className={`text-[10px] ${statusColor(mvStatus)}`}>
                            {mvStatus === "verified" ? labels.verified : mvStatus === "suspended" ? labels.suspended : mvStatus}
                          </Badge>
                        )}
                      </div>

                      <div className="space-y-1 text-xs text-gray-400">
                        {p.email && (
                          <p className="flex items-center gap-1.5">
                            <Mail className="w-3 h-3" /> {p.email}
                          </p>
                        )}
                        {p.phone && (
                          <p className="flex items-center gap-1.5">
                            <Phone className="w-3 h-3" /> {p.phone}
                          </p>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {tier && (
                          <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300">
                            {labels.tier}: {tier}
                          </Badge>
                        )}
                        {bg && (
                          <Badge variant="outline" className={`text-[10px] ${statusColor(bg)}`}>
                            <ShieldCheck className="w-3 h-3 mr-1" />
                            {labels.bgCheck}: {bg}
                          </Badge>
                        )}
                        {didit && (
                          <Badge variant="outline" className={`text-[10px] ${statusColor(didit)}`}>
                            <ShieldAlert className="w-3 h-3 mr-1" />
                            {labels.didit}: {didit}
                          </Badge>
                        )}
                      </div>

                      {licenses.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase text-gray-500 tracking-wide">{labels.licenses}</p>
                          <div className="space-y-0.5">
                            {licenses.map((l: any, i: number) => (
                              <p key={i} className="text-xs text-gray-300">
                                #{l.number}{l.details ? ` — ${l.details}` : ""}
                                {Array.isArray(l.jurisdictions) && l.jurisdictions.length > 0 && (
                                  <span className="text-gray-500"> ({l.jurisdictions.join(", ")})</span>
                                )}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      {specs.length > 0 && (
                        <div>
                          <p className="text-[10px] uppercase text-gray-500 tracking-wide">{labels.specializations}</p>
                          <div className="space-y-0.5">
                            {specs.map((s: any, i: number) => (
                              <p key={i} className="text-xs text-gray-300">
                                {Array.isArray(s.expertises) ? s.expertises.join(", ") : ""}
                                {s.industry ? ` — ${s.industry}` : ""}
                              </p>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex gap-2 pt-2 mt-auto">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === p.uid || mvStatus === "verified"}
                          onClick={() => setStatus(p.uid, "verified")}
                          className="flex-1 h-8 text-xs border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> {labels.verify}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busyId === p.uid || mvStatus === "suspended"}
                          onClick={() => setStatus(p.uid, "suspended")}
                          className="flex-1 h-8 text-xs border-red-500/40 text-red-300 hover:bg-red-500/10"
                        >
                          <Ban className="w-3.5 h-3.5 mr-1" /> {labels.suspend}
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {filtered.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">{labels.noPartners}</div>
                )}
              </div>
            </ScrollArea>
          )}
        </div>
      </div>
    </RequireStaff>
  );
}